// src/components/UploadPage.jsx
import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FileInput,
  TextInput,
} from "flowbite-react";
import {
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Badge,
} from "flowbite-react";
import { useNavigate } from "react-router-dom";
import { pdfjs } from "react-pdf";
import ReactSelect from 'react-select'
import { authFetch } from "../utils/authFetch";

pdfjs.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.mjs`;

export default function UploadPage({
  file,
  setFile,
  setFields,
  setOrder,
  setMapping,
  setUploadId,
}) {
  const navigate = useNavigate();

  // state for listing existing PDFs
  const [existingPdfs, setExistingPdfs] = useState([]);
  // modal open flag
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("quote");

  const categoryOptions = [
    { value: 'quote', label: 'Quote' },
    { value: 'other', label: 'Other' },
  ];
// inside UploadPage component, before the return:

  const handleToggleStatus = async (pdf) => {
    // Figure out what action we’re asking about
    const action = pdf.active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} "${pdf.name}"?`)) {
      return;
    }

    try {
      // call your API; adjust URL/method/body as needed
      const res = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/${pdf.id}/`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ active: !pdf.active }),
        }
      );

      if (!res.ok) throw new Error('Network response was not ok');

      const updatedPdf = await res.json();

      // update our table state
      setExistingPdfs((prev) =>
        prev.map((p) => (p.id === updatedPdf.id ? updatedPdf : p))
      );
    } catch (err) {
      console.error('Failed toggling status', err);
      alert('Sorry, could not update status.');
    }
  };

  // fetch list of PDFs on component mount
  useEffect(() => {
    async function loadPdfs() {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/list/`
        );
        const list = await res.json();
        setExistingPdfs(list);
      } catch (err) {
        console.error("Failed to load PDFs", err);
      }
    }
    loadPdfs();
  }, []);

  // open / close modal
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setNewName("");
  };

  // handle upload from within modal
  const handleModalUpload = async () => {
    if (!file) {
      alert("Please select a PDF file to upload.");
      return;
    }
    if (!newName.trim()) {
      alert("Please enter a name for the PDF.");
      return;
    }
    if (!selectedCategory) {
      alert("Please select a category.");
      return;
    }
    const form = new FormData();
    form.append("pdf_file", file);
    form.append("description", description);
    form.append("name", newName);
    form.append("category", selectedCategory);

    const res = await authFetch(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/pdf-generator/upload/`,
      {
        method: "POST",
        body: form,
      }
    );
    const json = await res.json();

    // send data to preview page
    setFields(json.fields);
    const keys = Object.keys(json.fields);
    setOrder(keys);
    setMapping(Object.fromEntries(keys.map((k) => [k, ""])));
    setUploadId(json.id);
    if (json.id) {
      navigate("/preview/" + json.id);
    }
  };

  return (
    <div className="flex flex-col px-5 mt-10 py-10 space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">PDF Generator</h2>
        {/* open upload modal */}
        <Button color="blue" onClick={openModal}>Upload New PDF</Button>
      </div>
      {/* table of existing PDFs */}
      <Table hoverable>
        <TableHead>
          <TableRow>
            <TableHeadCell>ID</TableHeadCell>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell>Category</TableHeadCell>
            <TableHeadCell>Uploaded At</TableHeadCell>
            <TableHeadCell>Status</TableHeadCell>
            <TableHeadCell>Actions</TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody className="divide-y">
          {existingPdfs.map((pdf) => (
            <TableRow key={pdf.id} className="bg-white">
              <TableCell>{pdf.id}</TableCell>
              <TableCell>{pdf.name}</TableCell>
              {/* show the label of the category from the categoryOptions */}
              <TableCell>{categoryOptions.find(option => option.value === pdf.category)?.label}</TableCell>
              <TableCell>{new Date(pdf.uploaded_at).toLocaleString()}</TableCell>
              <TableCell>
                <Badge
                  onClick={() => handleToggleStatus(pdf)}
                  style={{ cursor: 'pointer' }}
                  color={pdf.active ? 'green' : 'failure'}
                  size={pdf.active ? undefined : 'lg'}
                  className="w-24 flex items-center justify-center"
                >
                  {pdf.active ? 'Active' : 'Inactive'}
                </Badge>
              </TableCell>
              <TableCell>
                <Button color="light" onClick={() => {
                  navigate("/preview/" + pdf.id);
                }}>
                  Preview
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {existingPdfs.length === 0 && (
            <TableRow className="bg-white">
              <TableCell colSpan={4} className="text-center">
                No PDFs found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>


      {/* upload modal */}
      <Modal size="xl" show={isModalOpen} onClose={closeModal}>
        <ModalHeader>Upload PDF</ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <div>
              <Label htmlFor="pdf-name">PDF Name</Label>
              <TextInput
                id="pdf-name"
                placeholder="Enter a name..."
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <ReactSelect
                inputId="category"
                options={categoryOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#d1d5db', // Tailwind gray-300
                  }),
                }}
                value={selectedCategory ? { value: selectedCategory, label: selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1) } : null}
                onChange={opt => setSelectedCategory(opt.value)}
                isSearchable
                placeholder="Select category..."
                classNamePrefix="category"
              />
            </div>
            <div>
              <Label htmlFor="pdf-file">Select PDF</Label>
              <FileInput
                id="pdf-file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files[0])}
                required
              />
            </div>
            <div>
              <Label htmlFor="pdf-name">Description (Optional)</Label>
              <TextInput
                id="pdf-description"
                placeholder="Enter a description..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="blue" onClick={handleModalUpload}>
            Upload & Preview
          </Button>
          <Button color="light" onClick={closeModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
